/**
 * Global palette + tone token registry.
 *
 * Structure: PALETTES[color][tone][slot] = string[]
 *
 * Each slot value is an array of Tailwind classes (light + dark paired).
 * Tones are manually authored — no automatic scale derivation.
 *
 * Slots:
 *   bg          — surface / fill background
 *   bgSubtle    — lighter background for containers, panels
 *   text        — primary text
 *   textMuted   — secondary / subdued text
 *   border      — standard border
 *   borderSubtle — lighter border (e.g. root-level containers)
 *   icon        — icon foreground
 *   hoverBg     — background on hover
 *   activeBg    — background on press/active
 */

const neutral = {
	// bg-gray-50 → gray-100 → gray-200 with text darkening across tones
	soft: {
		bg: ['bg-gray-50', 'dark:bg-gray-800/50'],
		bgSubtle: ['bg-gray-50/40', 'dark:bg-gray-900/10'],
		text: ['text-gray-500', 'dark:text-gray-400'],
		textMuted: ['text-gray-400', 'dark:text-gray-500'],
		border: ['border-gray-200', 'dark:border-gray-700/50'],
		borderSubtle: ['border-gray-100', 'dark:border-gray-800/40'],
		icon: ['text-gray-400', 'dark:text-gray-500'],
		hoverBg: ['hover:bg-gray-100', 'dark:hover:bg-gray-700/60'],
		activeBg: ['active:bg-gray-200', 'dark:active:bg-gray-600/60'],
	},
	normal: {
		bg: ['bg-gray-100', 'dark:bg-gray-700/70'],
		bgSubtle: ['bg-gray-100/50', 'dark:bg-gray-800/30'],
		text: ['text-gray-600', 'dark:text-gray-300'],
		textMuted: ['text-gray-500', 'dark:text-gray-400'],
		border: ['border-gray-300', 'dark:border-gray-600'],
		borderSubtle: ['border-gray-200', 'dark:border-gray-700/40'],
		icon: ['text-gray-500', 'dark:text-gray-400'],
		hoverBg: ['hover:bg-gray-200', 'dark:hover:bg-gray-600/70'],
		activeBg: ['active:bg-gray-300', 'dark:active:bg-gray-500/70'],
	},
	strong: {
		bg: ['bg-gray-200', 'dark:bg-gray-600'],
		bgSubtle: ['bg-gray-200/80', 'dark:bg-gray-700/60'],
		text: ['text-gray-800', 'dark:text-gray-200'],
		textMuted: ['text-gray-600', 'dark:text-gray-300'],
		border: ['border-gray-400', 'dark:border-gray-500'],
		borderSubtle: ['border-gray-300', 'dark:border-gray-600'],
		icon: ['text-gray-700', 'dark:text-gray-200'],
		hoverBg: ['hover:bg-gray-300', 'dark:hover:bg-gray-500'],
		activeBg: ['active:bg-gray-400', 'dark:active:bg-gray-400'],
	},
};

const white = {
	soft: {
		bg: ['bg-white', 'dark:bg-gray-950'],
		bgSubtle: ['bg-gray-50', 'dark:bg-gray-900'],
		text: ['text-gray-600', 'dark:text-gray-300'],
		textMuted: ['text-gray-500', 'dark:text-gray-400'],
		border: ['border-gray-200', 'dark:border-gray-800'],
		borderSubtle: ['border-gray-100', 'dark:border-gray-800/60'],
		icon: ['text-gray-500', 'dark:text-gray-400'],
		hoverBg: ['hover:bg-gray-50', 'dark:hover:bg-gray-900'],
		activeBg: ['active:bg-gray-100', 'dark:active:bg-gray-800'],
	},
	normal: {
		bg: ['bg-white', 'dark:bg-gray-950'],
		bgSubtle: ['bg-gray-50', 'dark:bg-gray-900'],
		text: ['text-gray-700', 'dark:text-gray-200'],
		textMuted: ['text-gray-600', 'dark:text-gray-400'],
		border: ['border-gray-300', 'dark:border-gray-700'],
		borderSubtle: ['border-gray-200', 'dark:border-gray-800'],
		icon: ['text-gray-600', 'dark:text-gray-400'],
		hoverBg: ['hover:bg-gray-100', 'dark:hover:bg-gray-800'],
		activeBg: ['active:bg-gray-200', 'dark:active:bg-gray-800'],
	},
	strong: {
		bg: ['bg-white', 'dark:bg-gray-950'],
		bgSubtle: ['bg-gray-100', 'dark:bg-gray-900'],
		text: ['text-gray-900', 'dark:text-gray-100'],
		textMuted: ['text-gray-700', 'dark:text-gray-300'],
		border: ['border-gray-400', 'dark:border-gray-600'],
		borderSubtle: ['border-gray-300', 'dark:border-gray-700'],
		icon: ['text-gray-700', 'dark:text-gray-300'],
		hoverBg: ['hover:bg-gray-100', 'dark:hover:bg-gray-800'],
		activeBg: ['active:bg-gray-200', 'dark:active:bg-gray-800'],
	},
};

const blue = {
	soft: {
		bg: ['bg-blue-50', 'dark:bg-blue-950/40'],
		bgSubtle: ['bg-blue-50/30', 'dark:bg-blue-950/15'],
		text: ['text-blue-700', 'dark:text-blue-300'],
		textMuted: ['text-blue-600', 'dark:text-blue-400'],
		border: ['border-blue-200', 'dark:border-blue-700/70'],
		borderSubtle: ['border-blue-100', 'dark:border-blue-800/40'],
		icon: ['text-blue-700', 'dark:text-blue-300'],
		hoverBg: ['hover:bg-blue-100', 'dark:hover:bg-blue-900/50'],
		activeBg: ['active:bg-blue-200', 'dark:active:bg-blue-900/60'],
	},
	normal: {
		bg: ['bg-blue-100', 'dark:bg-blue-900/50'],
		bgSubtle: ['bg-blue-100/20', 'dark:bg-blue-900/20'],
		text: ['text-blue-800', 'dark:text-blue-200'],
		textMuted: ['text-blue-700', 'dark:text-blue-300'],
		border: ['border-blue-300', 'dark:border-blue-700/70'],
		borderSubtle: ['border-blue-200/50', 'dark:border-blue-700/40'],
		icon: ['text-blue-700', 'dark:text-blue-300'],
		hoverBg: ['hover:bg-blue-200', 'dark:hover:bg-blue-900/60'],
		activeBg: ['active:bg-blue-300', 'dark:active:bg-blue-900/70'],
	},
	strong: {
		bg: ['bg-blue-600', 'dark:bg-blue-700'],
		bgSubtle: ['bg-blue-500/80', 'dark:bg-blue-800/60'],
		text: ['text-white'],
		textMuted: ['text-blue-100', 'dark:text-blue-300'],
		border: ['border-blue-700', 'dark:border-blue-600'],
		borderSubtle: ['border-blue-600', 'dark:border-blue-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-blue-700', 'dark:hover:bg-blue-600'],
		activeBg: ['active:bg-blue-800', 'dark:active:bg-blue-500'],
	},
};

const orange = {
	soft: {
		bg: ['bg-orange-50', 'dark:bg-orange-950/40'],
		bgSubtle: ['bg-orange-50/30', 'dark:bg-orange-950/15'],
		text: ['text-orange-700', 'dark:text-orange-300'],
		textMuted: ['text-orange-600', 'dark:text-orange-400'],
		border: ['border-orange-200', 'dark:border-orange-700/70'],
		borderSubtle: ['border-orange-100', 'dark:border-orange-800/40'],
		icon: ['text-orange-700', 'dark:text-orange-300'],
		hoverBg: ['hover:bg-orange-100', 'dark:hover:bg-orange-900/50'],
		activeBg: ['active:bg-orange-200', 'dark:active:bg-orange-900/60'],
	},
	normal: {
		bg: ['bg-orange-100', 'dark:bg-orange-900/50'],
		bgSubtle: ['bg-orange-100/20', 'dark:bg-orange-900/20'],
		text: ['text-orange-800', 'dark:text-orange-200'],
		textMuted: ['text-orange-700', 'dark:text-orange-300'],
		border: ['border-orange-300', 'dark:border-orange-700/70'],
		borderSubtle: ['border-orange-200/50', 'dark:border-orange-700/40'],
		icon: ['text-orange-700', 'dark:text-orange-300'],
		hoverBg: ['hover:bg-orange-200', 'dark:hover:bg-orange-900/60'],
		activeBg: ['active:bg-orange-300', 'dark:active:bg-orange-900/70'],
	},
	strong: {
		bg: ['bg-orange-600', 'dark:bg-orange-700'],
		bgSubtle: ['bg-orange-500/80', 'dark:bg-orange-800/60'],
		text: ['text-white'],
		textMuted: ['text-orange-100', 'dark:text-orange-300'],
		border: ['border-orange-700', 'dark:border-orange-600'],
		borderSubtle: ['border-orange-600', 'dark:border-orange-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-orange-700', 'dark:hover:bg-orange-600'],
		activeBg: ['active:bg-orange-800', 'dark:active:bg-orange-500'],
	},
};

const green = {
	soft: {
		bg: ['bg-green-100', 'dark:bg-green-950/40'],
		bgSubtle: ['bg-green-50/50', 'dark:bg-green-950/20'],
		text: ['text-green-700', 'dark:text-green-300'],
		textMuted: ['text-green-600', 'dark:text-green-400'],
		border: ['border-green-300', 'dark:border-green-700/70'],
		borderSubtle: ['border-green-200', 'dark:border-green-800/50'],
		icon: ['text-green-700', 'dark:text-green-300'],
		hoverBg: ['hover:bg-green-200/70', 'dark:hover:bg-green-900/50'],
		activeBg: ['active:bg-green-200', 'dark:active:bg-green-900/60'],
	},
	normal: {
		bg: ['bg-green-200', 'dark:bg-green-900/50'],
		bgSubtle: ['bg-green-100/40', 'dark:bg-green-900/25'],
		text: ['text-green-800', 'dark:text-green-200'],
		textMuted: ['text-green-700', 'dark:text-green-300'],
		border: ['border-green-400/70', 'dark:border-green-700/70'],
		borderSubtle: ['border-green-300/60', 'dark:border-green-700/50'],
		icon: ['text-green-700', 'dark:text-green-300'],
		hoverBg: ['hover:bg-green-300', 'dark:hover:bg-green-900/60'],
		activeBg: ['active:bg-green-400', 'dark:active:bg-green-900/70'],
	},
	strong: {
		bg: ['bg-green-600', 'dark:bg-green-700'],
		bgSubtle: ['bg-green-500/80', 'dark:bg-green-800/60'],
		text: ['text-white'],
		textMuted: ['text-green-100', 'dark:text-green-300'],
		border: ['border-green-700', 'dark:border-green-600'],
		borderSubtle: ['border-green-600', 'dark:border-green-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-green-700', 'dark:hover:bg-green-600'],
		activeBg: ['active:bg-green-800', 'dark:active:bg-green-500'],
	},
};

const red = {
	soft: {
		bg: ['bg-red-50', 'dark:bg-red-950/40'],
		bgSubtle: ['bg-red-50/30', 'dark:bg-red-950/15'],
		text: ['text-red-700', 'dark:text-red-300'],
		textMuted: ['text-red-600', 'dark:text-red-400'],
		border: ['border-red-200', 'dark:border-red-700/70'],
		borderSubtle: ['border-red-100', 'dark:border-red-800/40'],
		icon: ['text-red-700', 'dark:text-red-300'],
		hoverBg: ['hover:bg-red-100', 'dark:hover:bg-red-900/50'],
		activeBg: ['active:bg-red-200', 'dark:active:bg-red-900/60'],
	},
	normal: {
		bg: ['bg-red-100', 'dark:bg-red-900/50'],
		bgSubtle: ['bg-red-100/20', 'dark:bg-red-900/20'],
		text: ['text-red-800', 'dark:text-red-200'],
		textMuted: ['text-red-700', 'dark:text-red-300'],
		border: ['border-red-300', 'dark:border-red-700/70'],
		borderSubtle: ['border-red-200/50', 'dark:border-red-700/40'],
		icon: ['text-red-700', 'dark:text-red-300'],
		hoverBg: ['hover:bg-red-200', 'dark:hover:bg-red-900/60'],
		activeBg: ['active:bg-red-300', 'dark:active:bg-red-900/70'],
	},
	strong: {
		bg: ['bg-red-600', 'dark:bg-red-700'],
		bgSubtle: ['bg-red-500/80', 'dark:bg-red-800/60'],
		text: ['text-white'],
		textMuted: ['text-red-100', 'dark:text-red-300'],
		border: ['border-red-700', 'dark:border-red-600'],
		borderSubtle: ['border-red-600', 'dark:border-red-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-red-700', 'dark:hover:bg-red-600'],
		activeBg: ['active:bg-red-800', 'dark:active:bg-red-500'],
	},
};

const purple = {
	soft: {
		bg: ['bg-purple-50', 'dark:bg-purple-950/40'],
		bgSubtle: ['bg-purple-50/30', 'dark:bg-purple-950/15'],
		text: ['text-purple-700', 'dark:text-purple-300'],
		textMuted: ['text-purple-600', 'dark:text-purple-400'],
		border: ['border-purple-200', 'dark:border-purple-700/70'],
		borderSubtle: ['border-purple-100', 'dark:border-purple-800/40'],
		icon: ['text-purple-700', 'dark:text-purple-300'],
		hoverBg: ['hover:bg-purple-100', 'dark:hover:bg-purple-900/50'],
		activeBg: ['active:bg-purple-200', 'dark:active:bg-purple-900/60'],
	},
	normal: {
		bg: ['bg-purple-100', 'dark:bg-purple-900/50'],
		bgSubtle: ['bg-purple-100/20', 'dark:bg-purple-900/20'],
		text: ['text-purple-800', 'dark:text-purple-200'],
		textMuted: ['text-purple-700', 'dark:text-purple-300'],
		border: ['border-purple-300', 'dark:border-purple-700/70'],
		borderSubtle: ['border-purple-200/50', 'dark:border-purple-700/40'],
		icon: ['text-purple-700', 'dark:text-purple-300'],
		hoverBg: ['hover:bg-purple-200', 'dark:hover:bg-purple-900/60'],
		activeBg: ['active:bg-purple-300', 'dark:active:bg-purple-900/70'],
	},
	strong: {
		bg: ['bg-purple-600', 'dark:bg-purple-700'],
		bgSubtle: ['bg-purple-500/80', 'dark:bg-purple-800/60'],
		text: ['text-white'],
		textMuted: ['text-purple-100', 'dark:text-purple-300'],
		border: ['border-purple-700', 'dark:border-purple-600'],
		borderSubtle: ['border-purple-600', 'dark:border-purple-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-purple-700', 'dark:hover:bg-purple-600'],
		activeBg: ['active:bg-purple-800', 'dark:active:bg-purple-500'],
	},
};

const sky = {
	soft: {
		bg: ['bg-sky-50', 'dark:bg-sky-950/40'],
		bgSubtle: ['bg-sky-50/30', 'dark:bg-sky-950/15'],
		text: ['text-sky-700', 'dark:text-sky-300'],
		textMuted: ['text-sky-600', 'dark:text-sky-400'],
		border: ['border-sky-200', 'dark:border-sky-700/70'],
		borderSubtle: ['border-sky-100', 'dark:border-sky-800/40'],
		icon: ['text-sky-700', 'dark:text-sky-300'],
		hoverBg: ['hover:bg-sky-100', 'dark:hover:bg-sky-900/50'],
		activeBg: ['active:bg-sky-200', 'dark:active:bg-sky-900/60'],
	},
	normal: {
		bg: ['bg-sky-100', 'dark:bg-sky-900/50'],
		bgSubtle: ['bg-sky-100/20', 'dark:bg-sky-900/20'],
		text: ['text-sky-800', 'dark:text-sky-200'],
		textMuted: ['text-sky-700', 'dark:text-sky-300'],
		border: ['border-sky-300', 'dark:border-sky-700/70'],
		borderSubtle: ['border-sky-200/50', 'dark:border-sky-700/40'],
		icon: ['text-sky-700', 'dark:text-sky-300'],
		hoverBg: ['hover:bg-sky-200', 'dark:hover:bg-sky-900/60'],
		activeBg: ['active:bg-sky-300', 'dark:active:bg-sky-900/70'],
	},
	strong: {
		bg: ['bg-sky-500', 'dark:bg-sky-600'],
		bgSubtle: ['bg-sky-400/80', 'dark:bg-sky-700/60'],
		text: ['text-white'],
		textMuted: ['text-sky-100', 'dark:text-sky-300'],
		border: ['border-sky-600', 'dark:border-sky-500'],
		borderSubtle: ['border-sky-500', 'dark:border-sky-600'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-sky-600', 'dark:hover:bg-sky-500'],
		activeBg: ['active:bg-sky-700', 'dark:active:bg-sky-400'],
	},
};

const indigo = {
	soft: {
		bg: ['bg-indigo-50', 'dark:bg-indigo-950/40'],
		bgSubtle: ['bg-indigo-50/30', 'dark:bg-indigo-950/15'],
		text: ['text-indigo-700', 'dark:text-indigo-300'],
		textMuted: ['text-indigo-600', 'dark:text-indigo-400'],
		border: ['border-indigo-200', 'dark:border-indigo-700/70'],
		borderSubtle: ['border-indigo-100', 'dark:border-indigo-800/40'],
		icon: ['text-indigo-700', 'dark:text-indigo-300'],
		hoverBg: ['hover:bg-indigo-100', 'dark:hover:bg-indigo-900/50'],
		activeBg: ['active:bg-indigo-200', 'dark:active:bg-indigo-900/60'],
	},
	normal: {
		bg: ['bg-indigo-100', 'dark:bg-indigo-900/50'],
		bgSubtle: ['bg-indigo-100/20', 'dark:bg-indigo-900/20'],
		text: ['text-indigo-800', 'dark:text-indigo-200'],
		textMuted: ['text-indigo-700', 'dark:text-indigo-300'],
		border: ['border-indigo-300', 'dark:border-indigo-700/70'],
		borderSubtle: ['border-indigo-200/50', 'dark:border-indigo-700/40'],
		icon: ['text-indigo-700', 'dark:text-indigo-300'],
		hoverBg: ['hover:bg-indigo-200', 'dark:hover:bg-indigo-900/60'],
		activeBg: ['active:bg-indigo-300', 'dark:active:bg-indigo-900/70'],
	},
	strong: {
		bg: ['bg-indigo-600', 'dark:bg-indigo-700'],
		bgSubtle: ['bg-indigo-500/80', 'dark:bg-indigo-800/60'],
		text: ['text-white'],
		textMuted: ['text-indigo-100', 'dark:text-indigo-300'],
		border: ['border-indigo-700', 'dark:border-indigo-600'],
		borderSubtle: ['border-indigo-600', 'dark:border-indigo-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-indigo-700', 'dark:hover:bg-indigo-600'],
		activeBg: ['active:bg-indigo-800', 'dark:active:bg-indigo-500'],
	},
};

const amber = {
	soft: {
		bg: ['bg-amber-50', 'dark:bg-amber-950/40'],
		bgSubtle: ['bg-amber-50/30', 'dark:bg-amber-950/15'],
		text: ['text-amber-700', 'dark:text-amber-300'],
		textMuted: ['text-amber-600', 'dark:text-amber-400'],
		border: ['border-amber-200', 'dark:border-amber-700/70'],
		borderSubtle: ['border-amber-100', 'dark:border-amber-800/40'],
		icon: ['text-amber-700', 'dark:text-amber-300'],
		hoverBg: ['hover:bg-amber-100', 'dark:hover:bg-amber-900/50'],
		activeBg: ['active:bg-amber-200', 'dark:active:bg-amber-900/60'],
	},
	normal: {
		bg: ['bg-amber-100', 'dark:bg-amber-900/50'],
		bgSubtle: ['bg-amber-100/20', 'dark:bg-amber-900/20'],
		text: ['text-amber-800', 'dark:text-amber-200'],
		textMuted: ['text-amber-700', 'dark:text-amber-300'],
		border: ['border-amber-300', 'dark:border-amber-700/70'],
		borderSubtle: ['border-amber-200/50', 'dark:border-amber-700/40'],
		icon: ['text-amber-700', 'dark:text-amber-300'],
		hoverBg: ['hover:bg-amber-200', 'dark:hover:bg-amber-900/60'],
		activeBg: ['active:bg-amber-300', 'dark:active:bg-amber-900/70'],
	},
	strong: {
		bg: ['bg-amber-500', 'dark:bg-amber-600'],
		bgSubtle: ['bg-amber-400/80', 'dark:bg-amber-700/60'],
		text: ['text-white'],
		textMuted: ['text-amber-100', 'dark:text-amber-300'],
		border: ['border-amber-600', 'dark:border-amber-500'],
		borderSubtle: ['border-amber-500', 'dark:border-amber-600'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-amber-600', 'dark:hover:bg-amber-500'],
		activeBg: ['active:bg-amber-700', 'dark:active:bg-amber-400'],
	},
};

const yellow = {
	soft: {
		bg: ['bg-yellow-50', 'dark:bg-yellow-950/40'],
		bgSubtle: ['bg-yellow-50/30', 'dark:bg-yellow-950/15'],
		text: ['text-yellow-700', 'dark:text-yellow-300'],
		textMuted: ['text-yellow-600', 'dark:text-yellow-400'],
		border: ['border-yellow-200', 'dark:border-yellow-700/70'],
		borderSubtle: ['border-yellow-100', 'dark:border-yellow-800/40'],
		icon: ['text-yellow-700', 'dark:text-yellow-300'],
		hoverBg: ['hover:bg-yellow-100', 'dark:hover:bg-yellow-900/50'],
		activeBg: ['active:bg-yellow-200', 'dark:active:bg-yellow-900/60'],
	},
	normal: {
		bg: ['bg-yellow-100', 'dark:bg-yellow-900/50'],
		bgSubtle: ['bg-yellow-100/20', 'dark:bg-yellow-900/20'],
		text: ['text-yellow-800', 'dark:text-yellow-200'],
		textMuted: ['text-yellow-700', 'dark:text-yellow-300'],
		border: ['border-yellow-300', 'dark:border-yellow-700/70'],
		borderSubtle: ['border-yellow-200/50', 'dark:border-yellow-700/40'],
		icon: ['text-yellow-700', 'dark:text-yellow-300'],
		hoverBg: ['hover:bg-yellow-200', 'dark:hover:bg-yellow-900/60'],
		activeBg: ['active:bg-yellow-300', 'dark:active:bg-yellow-900/70'],
	},
	strong: {
		bg: ['bg-yellow-400', 'dark:bg-yellow-500'],
		bgSubtle: ['bg-yellow-300/80', 'dark:bg-yellow-600/60'],
		text: ['text-yellow-900', 'dark:text-yellow-900'],
		textMuted: ['text-yellow-800', 'dark:text-yellow-800'],
		border: ['border-yellow-500', 'dark:border-yellow-400'],
		borderSubtle: ['border-yellow-400', 'dark:border-yellow-500'],
		icon: ['text-yellow-900', 'dark:text-yellow-900'],
		hoverBg: ['hover:bg-yellow-500', 'dark:hover:bg-yellow-400'],
		activeBg: ['active:bg-yellow-600', 'dark:active:bg-yellow-300'],
	},
};

const fuchsia = {
	soft: {
		bg: ['bg-fuchsia-50', 'dark:bg-fuchsia-950/40'],
		bgSubtle: ['bg-fuchsia-50/30', 'dark:bg-fuchsia-950/15'],
		text: ['text-fuchsia-700', 'dark:text-fuchsia-300'],
		textMuted: ['text-fuchsia-600', 'dark:text-fuchsia-400'],
		border: ['border-fuchsia-200', 'dark:border-fuchsia-700/70'],
		borderSubtle: ['border-fuchsia-100', 'dark:border-fuchsia-800/40'],
		icon: ['text-fuchsia-700', 'dark:text-fuchsia-300'],
		hoverBg: ['hover:bg-fuchsia-100', 'dark:hover:bg-fuchsia-900/50'],
		activeBg: ['active:bg-fuchsia-200', 'dark:active:bg-fuchsia-900/60'],
	},
	normal: {
		bg: ['bg-fuchsia-100', 'dark:bg-fuchsia-900/50'],
		bgSubtle: ['bg-fuchsia-100/20', 'dark:bg-fuchsia-900/20'],
		text: ['text-fuchsia-800', 'dark:text-fuchsia-200'],
		textMuted: ['text-fuchsia-700', 'dark:text-fuchsia-300'],
		border: ['border-fuchsia-300', 'dark:border-fuchsia-700/70'],
		borderSubtle: ['border-fuchsia-200/50', 'dark:border-fuchsia-700/40'],
		icon: ['text-fuchsia-700', 'dark:text-fuchsia-300'],
		hoverBg: ['hover:bg-fuchsia-200', 'dark:hover:bg-fuchsia-900/60'],
		activeBg: ['active:bg-fuchsia-300', 'dark:active:bg-fuchsia-900/70'],
	},
	strong: {
		bg: ['bg-fuchsia-600', 'dark:bg-fuchsia-700'],
		bgSubtle: ['bg-fuchsia-500/80', 'dark:bg-fuchsia-800/60'],
		text: ['text-white'],
		textMuted: ['text-fuchsia-100', 'dark:text-fuchsia-300'],
		border: ['border-fuchsia-700', 'dark:border-fuchsia-600'],
		borderSubtle: ['border-fuchsia-600', 'dark:border-fuchsia-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-fuchsia-700', 'dark:hover:bg-fuchsia-600'],
		activeBg: ['active:bg-fuchsia-800', 'dark:active:bg-fuchsia-500'],
	},
};

const slate = {
	soft: {
		bg: ['bg-slate-100', 'dark:bg-slate-800'],
		bgSubtle: ['bg-slate-50/30', 'dark:bg-slate-900/10'],
		text: ['text-slate-600', 'dark:text-slate-300'],
		textMuted: ['text-slate-500', 'dark:text-slate-400'],
		border: ['border-slate-200', 'dark:border-slate-700/60'],
		borderSubtle: ['border-slate-200', 'dark:border-slate-700/60'],
		icon: ['text-slate-500', 'dark:text-slate-400'],
		hoverBg: ['hover:bg-slate-200', 'dark:hover:bg-slate-700'],
		activeBg: ['active:bg-slate-300', 'dark:active:bg-slate-600'],
	},
	normal: {
		bg: ['bg-slate-200', 'dark:bg-slate-700'],
		bgSubtle: ['bg-slate-100/50', 'dark:bg-slate-800/30'],
		text: ['text-slate-700', 'dark:text-slate-200'],
		textMuted: ['text-slate-600', 'dark:text-slate-400'],
		border: ['border-slate-300', 'dark:border-slate-600'],
		borderSubtle: ['border-slate-200/50', 'dark:border-slate-700/30'],
		icon: ['text-slate-600', 'dark:text-slate-400'],
		hoverBg: ['hover:bg-slate-300', 'dark:hover:bg-slate-600'],
		activeBg: ['active:bg-slate-400', 'dark:active:bg-slate-500'],
	},
	strong: {
		bg: ['bg-slate-700', 'dark:bg-slate-600'],
		bgSubtle: ['bg-slate-600/80', 'dark:bg-slate-700/60'],
		text: ['text-white'],
		textMuted: ['text-slate-300', 'dark:text-slate-400'],
		border: ['border-slate-800', 'dark:border-slate-500'],
		borderSubtle: ['border-slate-700', 'dark:border-slate-600'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-slate-800', 'dark:hover:bg-slate-500'],
		activeBg: ['active:bg-slate-900', 'dark:active:bg-slate-400'],
	},
};

const zinc = {
	soft: {
		bg: ['bg-zinc-100', 'dark:bg-zinc-800'],
		bgSubtle: ['bg-zinc-50/30', 'dark:bg-zinc-900/10'],
		text: ['text-zinc-600', 'dark:text-zinc-300'],
		textMuted: ['text-zinc-500', 'dark:text-zinc-400'],
		border: ['border-zinc-200', 'dark:border-zinc-700/60'],
		borderSubtle: ['border-zinc-200', 'dark:border-zinc-700/60'],
		icon: ['text-zinc-500', 'dark:text-zinc-400'],
		hoverBg: ['hover:bg-zinc-200', 'dark:hover:bg-zinc-700'],
		activeBg: ['active:bg-zinc-300', 'dark:active:bg-zinc-600'],
	},
	normal: {
		bg: ['bg-zinc-200', 'dark:bg-zinc-700'],
		bgSubtle: ['bg-zinc-100/50', 'dark:bg-zinc-800/30'],
		text: ['text-zinc-700', 'dark:text-zinc-200'],
		textMuted: ['text-zinc-600', 'dark:text-zinc-400'],
		border: ['border-zinc-300', 'dark:border-zinc-600'],
		borderSubtle: ['border-zinc-200/50', 'dark:border-zinc-700/30'],
		icon: ['text-zinc-600', 'dark:text-zinc-400'],
		hoverBg: ['hover:bg-zinc-300', 'dark:hover:bg-zinc-600'],
		activeBg: ['active:bg-zinc-400', 'dark:active:bg-zinc-500'],
	},
	strong: {
		bg: ['bg-zinc-700', 'dark:bg-zinc-600'],
		bgSubtle: ['bg-zinc-600/80', 'dark:bg-zinc-700/60'],
		text: ['text-white'],
		textMuted: ['text-zinc-300', 'dark:text-zinc-400'],
		border: ['border-zinc-800', 'dark:border-zinc-500'],
		borderSubtle: ['border-zinc-700', 'dark:border-zinc-600'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-zinc-800', 'dark:hover:bg-zinc-500'],
		activeBg: ['active:bg-zinc-900', 'dark:active:bg-zinc-400'],
	},
};

const stone = {
	soft: {
		bg: ['bg-stone-100', 'dark:bg-stone-800'],
		bgSubtle: ['bg-stone-50/30', 'dark:bg-stone-900/10'],
		text: ['text-stone-600', 'dark:text-stone-300'],
		textMuted: ['text-stone-500', 'dark:text-stone-400'],
		border: ['border-stone-200', 'dark:border-stone-700/60'],
		borderSubtle: ['border-stone-200', 'dark:border-stone-700/60'],
		icon: ['text-stone-500', 'dark:text-stone-400'],
		hoverBg: ['hover:bg-stone-200', 'dark:hover:bg-stone-700'],
		activeBg: ['active:bg-stone-300', 'dark:active:bg-stone-600'],
	},
	normal: {
		bg: ['bg-stone-200', 'dark:bg-stone-700'],
		bgSubtle: ['bg-stone-100/50', 'dark:bg-stone-800/30'],
		text: ['text-stone-700', 'dark:text-stone-200'],
		textMuted: ['text-stone-600', 'dark:text-stone-400'],
		border: ['border-stone-300', 'dark:border-stone-600'],
		borderSubtle: ['border-stone-200/50', 'dark:border-stone-700/30'],
		icon: ['text-stone-600', 'dark:text-stone-400'],
		hoverBg: ['hover:bg-stone-300', 'dark:hover:bg-stone-600'],
		activeBg: ['active:bg-stone-400', 'dark:active:bg-stone-500'],
	},
	strong: {
		bg: ['bg-stone-700', 'dark:bg-stone-600'],
		bgSubtle: ['bg-stone-600/80', 'dark:bg-stone-700/60'],
		text: ['text-white'],
		textMuted: ['text-stone-300', 'dark:text-stone-400'],
		border: ['border-stone-800', 'dark:border-stone-500'],
		borderSubtle: ['border-stone-700', 'dark:border-stone-600'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-stone-800', 'dark:hover:bg-stone-500'],
		activeBg: ['active:bg-stone-900', 'dark:active:bg-stone-400'],
	},
};

const lime = {
	soft: {
		bg: ['bg-lime-50', 'dark:bg-lime-950/40'],
		bgSubtle: ['bg-lime-50/30', 'dark:bg-lime-950/15'],
		text: ['text-lime-700', 'dark:text-lime-300'],
		textMuted: ['text-lime-600', 'dark:text-lime-400'],
		border: ['border-lime-200', 'dark:border-lime-700/70'],
		borderSubtle: ['border-lime-100', 'dark:border-lime-800/40'],
		icon: ['text-lime-700', 'dark:text-lime-300'],
		hoverBg: ['hover:bg-lime-100', 'dark:hover:bg-lime-900/50'],
		activeBg: ['active:bg-lime-200', 'dark:active:bg-lime-900/60'],
	},
	normal: {
		bg: ['bg-lime-100', 'dark:bg-lime-900/50'],
		bgSubtle: ['bg-lime-100/20', 'dark:bg-lime-900/20'],
		text: ['text-lime-800', 'dark:text-lime-200'],
		textMuted: ['text-lime-700', 'dark:text-lime-300'],
		border: ['border-lime-300', 'dark:border-lime-700/70'],
		borderSubtle: ['border-lime-200/50', 'dark:border-lime-700/40'],
		icon: ['text-lime-700', 'dark:text-lime-300'],
		hoverBg: ['hover:bg-lime-200', 'dark:hover:bg-lime-900/60'],
		activeBg: ['active:bg-lime-300', 'dark:active:bg-lime-900/70'],
	},
	strong: {
		bg: ['bg-lime-500', 'dark:bg-lime-600'],
		bgSubtle: ['bg-lime-400/80', 'dark:bg-lime-700/60'],
		text: ['text-white'],
		textMuted: ['text-lime-100', 'dark:text-lime-300'],
		border: ['border-lime-600', 'dark:border-lime-500'],
		borderSubtle: ['border-lime-500', 'dark:border-lime-600'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-lime-600', 'dark:hover:bg-lime-500'],
		activeBg: ['active:bg-lime-700', 'dark:active:bg-lime-400'],
	},
};

const emerald = {
	soft: {
		bg: ['bg-emerald-50', 'dark:bg-emerald-950/40'],
		bgSubtle: ['bg-emerald-50/30', 'dark:bg-emerald-950/15'],
		text: ['text-emerald-700', 'dark:text-emerald-300'],
		textMuted: ['text-emerald-600', 'dark:text-emerald-400'],
		border: ['border-emerald-200', 'dark:border-emerald-700/70'],
		borderSubtle: ['border-emerald-100', 'dark:border-emerald-800/40'],
		icon: ['text-emerald-700', 'dark:text-emerald-300'],
		hoverBg: ['hover:bg-emerald-100', 'dark:hover:bg-emerald-900/50'],
		activeBg: ['active:bg-emerald-200', 'dark:active:bg-emerald-900/60'],
	},
	normal: {
		bg: ['bg-emerald-100', 'dark:bg-emerald-900/50'],
		bgSubtle: ['bg-emerald-100/20', 'dark:bg-emerald-900/20'],
		text: ['text-emerald-800', 'dark:text-emerald-200'],
		textMuted: ['text-emerald-700', 'dark:text-emerald-300'],
		border: ['border-emerald-300', 'dark:border-emerald-700/70'],
		borderSubtle: ['border-emerald-200/50', 'dark:border-emerald-700/40'],
		icon: ['text-emerald-700', 'dark:text-emerald-300'],
		hoverBg: ['hover:bg-emerald-200', 'dark:hover:bg-emerald-900/60'],
		activeBg: ['active:bg-emerald-300', 'dark:active:bg-emerald-900/70'],
	},
	strong: {
		bg: ['bg-emerald-600', 'dark:bg-emerald-700'],
		bgSubtle: ['bg-emerald-500/80', 'dark:bg-emerald-800/60'],
		text: ['text-white'],
		textMuted: ['text-emerald-100', 'dark:text-emerald-300'],
		border: ['border-emerald-700', 'dark:border-emerald-600'],
		borderSubtle: ['border-emerald-600', 'dark:border-emerald-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-emerald-700', 'dark:hover:bg-emerald-600'],
		activeBg: ['active:bg-emerald-800', 'dark:active:bg-emerald-500'],
	},
};

const teal = {
	soft: {
		bg: ['bg-teal-50', 'dark:bg-teal-950/40'],
		bgSubtle: ['bg-teal-50/30', 'dark:bg-teal-950/15'],
		text: ['text-teal-700', 'dark:text-teal-300'],
		textMuted: ['text-teal-600', 'dark:text-teal-400'],
		border: ['border-teal-200', 'dark:border-teal-700/70'],
		borderSubtle: ['border-teal-100', 'dark:border-teal-800/40'],
		icon: ['text-teal-700', 'dark:text-teal-300'],
		hoverBg: ['hover:bg-teal-100', 'dark:hover:bg-teal-900/50'],
		activeBg: ['active:bg-teal-200', 'dark:active:bg-teal-900/60'],
	},
	normal: {
		bg: ['bg-teal-100', 'dark:bg-teal-900/50'],
		bgSubtle: ['bg-teal-100/20', 'dark:bg-teal-900/20'],
		text: ['text-teal-800', 'dark:text-teal-200'],
		textMuted: ['text-teal-700', 'dark:text-teal-300'],
		border: ['border-teal-300', 'dark:border-teal-700/70'],
		borderSubtle: ['border-teal-200/50', 'dark:border-teal-700/40'],
		icon: ['text-teal-700', 'dark:text-teal-300'],
		hoverBg: ['hover:bg-teal-200', 'dark:hover:bg-teal-900/60'],
		activeBg: ['active:bg-teal-300', 'dark:active:bg-teal-900/70'],
	},
	strong: {
		bg: ['bg-teal-600', 'dark:bg-teal-700'],
		bgSubtle: ['bg-teal-500/80', 'dark:bg-teal-800/60'],
		text: ['text-white'],
		textMuted: ['text-teal-100', 'dark:text-teal-300'],
		border: ['border-teal-700', 'dark:border-teal-600'],
		borderSubtle: ['border-teal-600', 'dark:border-teal-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-teal-700', 'dark:hover:bg-teal-600'],
		activeBg: ['active:bg-teal-800', 'dark:active:bg-teal-500'],
	},
};

const cyan = {
	soft: {
		bg: ['bg-cyan-50', 'dark:bg-cyan-950/40'],
		bgSubtle: ['bg-cyan-50/30', 'dark:bg-cyan-950/15'],
		text: ['text-cyan-700', 'dark:text-cyan-300'],
		textMuted: ['text-cyan-600', 'dark:text-cyan-400'],
		border: ['border-cyan-200', 'dark:border-cyan-700/70'],
		borderSubtle: ['border-cyan-100', 'dark:border-cyan-800/40'],
		icon: ['text-cyan-700', 'dark:text-cyan-300'],
		hoverBg: ['hover:bg-cyan-100', 'dark:hover:bg-cyan-900/50'],
		activeBg: ['active:bg-cyan-200', 'dark:active:bg-cyan-900/60'],
	},
	normal: {
		bg: ['bg-cyan-100', 'dark:bg-cyan-900/50'],
		bgSubtle: ['bg-cyan-100/20', 'dark:bg-cyan-900/20'],
		text: ['text-cyan-800', 'dark:text-cyan-200'],
		textMuted: ['text-cyan-700', 'dark:text-cyan-300'],
		border: ['border-cyan-300', 'dark:border-cyan-700/70'],
		borderSubtle: ['border-cyan-200/50', 'dark:border-cyan-700/40'],
		icon: ['text-cyan-700', 'dark:text-cyan-300'],
		hoverBg: ['hover:bg-cyan-200', 'dark:hover:bg-cyan-900/60'],
		activeBg: ['active:bg-cyan-300', 'dark:active:bg-cyan-900/70'],
	},
	strong: {
		bg: ['bg-cyan-600', 'dark:bg-cyan-700'],
		bgSubtle: ['bg-cyan-500/80', 'dark:bg-cyan-800/60'],
		text: ['text-white'],
		textMuted: ['text-cyan-100', 'dark:text-cyan-300'],
		border: ['border-cyan-700', 'dark:border-cyan-600'],
		borderSubtle: ['border-cyan-600', 'dark:border-cyan-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-cyan-700', 'dark:hover:bg-cyan-600'],
		activeBg: ['active:bg-cyan-800', 'dark:active:bg-cyan-500'],
	},
};

const violet = {
	soft: {
		bg: ['bg-violet-50', 'dark:bg-violet-950/40'],
		bgSubtle: ['bg-violet-50/30', 'dark:bg-violet-950/15'],
		text: ['text-violet-700', 'dark:text-violet-300'],
		textMuted: ['text-violet-600', 'dark:text-violet-400'],
		border: ['border-violet-200', 'dark:border-violet-700/70'],
		borderSubtle: ['border-violet-100', 'dark:border-violet-800/40'],
		icon: ['text-violet-700', 'dark:text-violet-300'],
		hoverBg: ['hover:bg-violet-100', 'dark:hover:bg-violet-900/50'],
		activeBg: ['active:bg-violet-200', 'dark:active:bg-violet-900/60'],
	},
	normal: {
		bg: ['bg-violet-100', 'dark:bg-violet-900/50'],
		bgSubtle: ['bg-violet-100/20', 'dark:bg-violet-900/20'],
		text: ['text-violet-800', 'dark:text-violet-200'],
		textMuted: ['text-violet-700', 'dark:text-violet-300'],
		border: ['border-violet-300', 'dark:border-violet-700/70'],
		borderSubtle: ['border-violet-200/50', 'dark:border-violet-700/40'],
		icon: ['text-violet-700', 'dark:text-violet-300'],
		hoverBg: ['hover:bg-violet-200', 'dark:hover:bg-violet-900/60'],
		activeBg: ['active:bg-violet-300', 'dark:active:bg-violet-900/70'],
	},
	strong: {
		bg: ['bg-violet-600', 'dark:bg-violet-700'],
		bgSubtle: ['bg-violet-500/80', 'dark:bg-violet-800/60'],
		text: ['text-white'],
		textMuted: ['text-violet-100', 'dark:text-violet-300'],
		border: ['border-violet-700', 'dark:border-violet-600'],
		borderSubtle: ['border-violet-600', 'dark:border-violet-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-violet-700', 'dark:hover:bg-violet-600'],
		activeBg: ['active:bg-violet-800', 'dark:active:bg-violet-500'],
	},
};

const pink = {
	soft: {
		bg: ['bg-pink-50', 'dark:bg-pink-950/40'],
		bgSubtle: ['bg-pink-50/30', 'dark:bg-pink-950/15'],
		text: ['text-pink-700', 'dark:text-pink-300'],
		textMuted: ['text-pink-600', 'dark:text-pink-400'],
		border: ['border-pink-200', 'dark:border-pink-700/70'],
		borderSubtle: ['border-pink-100', 'dark:border-pink-800/40'],
		icon: ['text-pink-700', 'dark:text-pink-300'],
		hoverBg: ['hover:bg-pink-100', 'dark:hover:bg-pink-900/50'],
		activeBg: ['active:bg-pink-200', 'dark:active:bg-pink-900/60'],
	},
	normal: {
		bg: ['bg-pink-100', 'dark:bg-pink-900/50'],
		bgSubtle: ['bg-pink-100/20', 'dark:bg-pink-900/20'],
		text: ['text-pink-800', 'dark:text-pink-200'],
		textMuted: ['text-pink-700', 'dark:text-pink-300'],
		border: ['border-pink-300', 'dark:border-pink-700/70'],
		borderSubtle: ['border-pink-200/50', 'dark:border-pink-700/40'],
		icon: ['text-pink-700', 'dark:text-pink-300'],
		hoverBg: ['hover:bg-pink-200', 'dark:hover:bg-pink-900/60'],
		activeBg: ['active:bg-pink-300', 'dark:active:bg-pink-900/70'],
	},
	strong: {
		bg: ['bg-pink-600', 'dark:bg-pink-700'],
		bgSubtle: ['bg-pink-500/80', 'dark:bg-pink-800/60'],
		text: ['text-white'],
		textMuted: ['text-pink-100', 'dark:text-pink-300'],
		border: ['border-pink-700', 'dark:border-pink-600'],
		borderSubtle: ['border-pink-600', 'dark:border-pink-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-pink-700', 'dark:hover:bg-pink-600'],
		activeBg: ['active:bg-pink-800', 'dark:active:bg-pink-500'],
	},
};

const rose = {
	soft: {
		bg: ['bg-rose-50', 'dark:bg-rose-950/40'],
		bgSubtle: ['bg-rose-50/30', 'dark:bg-rose-950/15'],
		text: ['text-rose-700', 'dark:text-rose-300'],
		textMuted: ['text-rose-600', 'dark:text-rose-400'],
		border: ['border-rose-200', 'dark:border-rose-700/70'],
		borderSubtle: ['border-rose-100', 'dark:border-rose-800/40'],
		icon: ['text-rose-700', 'dark:text-rose-300'],
		hoverBg: ['hover:bg-rose-100', 'dark:hover:bg-rose-900/50'],
		activeBg: ['active:bg-rose-200', 'dark:active:bg-rose-900/60'],
	},
	normal: {
		bg: ['bg-rose-100', 'dark:bg-rose-900/50'],
		bgSubtle: ['bg-rose-100/20', 'dark:bg-rose-900/20'],
		text: ['text-rose-800', 'dark:text-rose-200'],
		textMuted: ['text-rose-700', 'dark:text-rose-300'],
		border: ['border-rose-300', 'dark:border-rose-700/70'],
		borderSubtle: ['border-rose-200/50', 'dark:border-rose-700/40'],
		icon: ['text-rose-700', 'dark:text-rose-300'],
		hoverBg: ['hover:bg-rose-200', 'dark:hover:bg-rose-900/60'],
		activeBg: ['active:bg-rose-300', 'dark:active:bg-rose-900/70'],
	},
	strong: {
		bg: ['bg-rose-600', 'dark:bg-rose-700'],
		bgSubtle: ['bg-rose-500/80', 'dark:bg-rose-800/60'],
		text: ['text-white'],
		textMuted: ['text-rose-100', 'dark:text-rose-300'],
		border: ['border-rose-700', 'dark:border-rose-600'],
		borderSubtle: ['border-rose-600', 'dark:border-rose-700'],
		icon: ['text-white'],
		hoverBg: ['hover:bg-rose-700', 'dark:hover:bg-rose-600'],
		activeBg: ['active:bg-rose-800', 'dark:active:bg-rose-500'],
	},
};

export const PALETTES = {
	neutral,
	white,
	slate,
	zinc,
	stone,
	red,
	orange,
	amber,
	yellow,
	lime,
	green,
	emerald,
	teal,
	cyan,
	sky,
	blue,
	indigo,
	violet,
	purple,
	fuchsia,
	pink,
	rose,
};

export const PALETTE_NAMES = Object.keys(PALETTES);

export const TONE_NAMES = ['soft', 'normal', 'strong'];

export const COLOR_ALIASES = {};

export const getPalette = (color, tone) =>
	PALETTES[color]?.[tone] || PALETTES.neutral.soft;

export const getSlot = (color, tone, slot) => getPalette(color, tone)?.[slot] || [];
